import { getGSAPSettings } from '../utils/GSAP';
import { logWarn, showErrorOnElement } from './errors.js';

const checkStaggerElements = document.querySelectorAll('[vdx-gsap-staggerin]');

if (checkStaggerElements.length > 0){
    const firstEl = checkStaggerElements[0];
    if (typeof gsap === 'undefined') {
        logWarn('GSAP is missing. Please turn on GSAP in Webflow.', { module: 'GSAPStagger' });
        showErrorOnElement(firstEl, 'GSAP is missing. Please turn on GSAP in Webflow.');
    }
    if (typeof ScrollTrigger === 'undefined') {
        logWarn('ScrollTrigger not found. Activate it in Webflow for animations.', { module: 'GSAPStagger' });
        showErrorOnElement(firstEl, 'ScrollTrigger not found. Activate it in Webflow for animations.');
    }
}

if (checkStaggerElements.length > 0 && typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined'){
    gsap.registerPlugin(ScrollTrigger);
}

export const staggerIn = () => {
    if (checkStaggerElements.length > 0 && typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined'){       
        const staggerContainers = gsap.utils.toArray('[vdx-gsap-staggerin]');
        if (staggerContainers.length === 0) {
            return;
    }
    
    // Process each container separately to handle different animation types
    staggerContainers.forEach(container => {
        if (!container.children || container.children.length === 0) {
            return;
        }
        
        // Get animation type from the attribute
        const animationType = container.getAttribute('vdx-gsap-staggerin') || 'fade';
        
        // Check if animation should be disabled (value is 'false')
        if (animationType === 'false') {
            // Make all children visible without animation
            const children = Array.from(container.children);
            gsap.set(children, { opacity: 1, clearProps: "x,y,scale,rotation,rotationX" });
            return;
        }
        const { duration, stagger, ease, disableBreakpoint } = getGSAPSettings(container);
        // Check if animations should be disabled based on breakpoint
        if (disableBreakpoint > 0 && window.innerWidth <= disableBreakpoint) {
            // Make all children visible without animation
            const children = Array.from(container.children);
            gsap.set(children, { opacity: 1, clearProps: "x,y,scale,rotation,rotationX" });
            return;
        }
        
        // Get all children for this specific container
        const children = Array.from(container.children);
        
        // Set initial state for all children at once
        switch(animationType) {
            case 'slide-up':
                gsap.set(children, { opacity: 0, y: 50 });
                break;
            case 'slide-down':
                gsap.set(children, { opacity: 0, y: -50 });
                break;
            case 'slide-left':
                gsap.set(children, { opacity: 0, x: 50 });
                break;
            case 'slide-right':
                gsap.set(children, { opacity: 0, x: -50 });
                break;
            case 'scale':
                gsap.set(children, { opacity: 0, scale: 0 });
                break;
            case 'rotate':
                gsap.set(children, { opacity: 0, rotation: 45 });
                break;
            case 'bounce':
                gsap.set(children, { opacity: 0, y: 50 });
                break;
            case 'flip':
                gsap.set(children, { opacity: 0, rotationX: 90 });
                break;
            case 'zoom':
                gsap.set(children, { opacity: 0, scale: 1.5 });
                break;
            default:
                gsap.set(children, { opacity: 0 });
        }
        
        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.1,
        };
        
        // Create a timeline for this container
        const tl = gsap.timeline({ paused: true });
        
        // Add animations to the timeline based on animation type
        switch(animationType) {
            case 'fade':
                tl.to(children, {
                    opacity: 1,
                    duration: duration,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'slide-up':
                tl.to(children, {
                    y: 0,
                    opacity: 1,
                    duration: duration,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'slide-down':
                tl.to(children, {
                    y: 0,
                    opacity: 1,
                    duration: duration,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'slide-left':
                tl.to(children, {
                    x: 0,
                    opacity: 1,
                    duration: duration,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'slide-right':
                tl.to(children, {
                    x: 0,
                    opacity: 1,
                    duration: duration,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'scale':
                tl.to(children, {
                    scale: 1,
                    opacity: 1,
                    duration: duration,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'rotate':
                tl.to(children, {
                    rotation: 0,
                    opacity: 1,
                    duration: duration,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'bounce':
                tl.to(children, {
                    y: 0,
                    opacity: 1,
                    duration: duration * 1.5,
                    stagger: stagger,
                    ease: "bounce.out"
                });
                break;
                
            case 'flip':
                tl.to(children, {
                    rotationX: 0,
                    opacity: 1,
                    duration: duration,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'zoom':
                tl.to(children, {
                    scale: 1,
                    opacity: 1,
                    duration: duration,
                    stagger: stagger,
                    ease: "back.out(1.7)"
                });
                break;
                
            default:
                tl.to(children, {
                    opacity: 1,
                    duration: duration,
                    stagger: stagger,
                    ease: ease
                });
        }
        
        // Create observer for this container
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    tl.play();
                    observer.unobserve(container);
                }
            });
        }, observerOptions);
        
        // Observe the container instead of individual children
        observer.observe(container);
    });
    }
};